import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faLinkSlash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"

const VIEWMEDICA_HOST = /(^|\.)viewmedica\.com$/i

const getOpenLabel = (link) => {
  try {
    return VIEWMEDICA_HOST.test(new URL(link).hostname)
      ? "Open in ViewMedica"
      : "Open in new tab"
  } catch {
    return "Open in new tab"
  }
}

// Hover this long before anything loads, so sweeping the mouse down the
// table doesn't start a dozen video players.
const OPEN_DELAY_MS = 600
// Time to move the mouse from the link onto the tooltip without it closing.
const CLOSE_DELAY_MS = 200
// No `load` from the player by then counts as a broken link.
const LOAD_TIMEOUT_MS = 10000

const TOOLTIP_WIDTH = 384
const TOOLTIP_HEIGHT = 260
const GAP = 8

const linkStatuses = Object.freeze({
  OK: "ok",
  BROKEN: "broken",
  NOT_EMBEDDABLE: "not_embeddable",
})

// Below the link when it fits, otherwise above; always inside the window.
const placeTooltip = (anchor) => {
  const rect = anchor.getBoundingClientRect()
  const fitsBelow =
    rect.bottom + GAP + TOOLTIP_HEIGHT <= window.innerHeight ||
    rect.top < TOOLTIP_HEIGHT + GAP
  const left = Math.min(
    Math.max(GAP, rect.left),
    window.innerWidth - TOOLTIP_WIDTH - GAP
  )

  return {
    left,
    top: fitsBelow ? rect.bottom + GAP : rect.top - GAP - TOOLTIP_HEIGHT,
  }
}

const Notice = ({ icon, tone, title, children, link }) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
    <FontAwesomeIcon className={`h-6 w-6 ${tone}`} icon={icon} />
    <p className="text-sm font-semibold text-gray-800">{title}</p>
    {children && <p className="text-xs text-gray-500">{children}</p>}
    {link && (
      <a
        className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
        href={link}
        rel="noreferrer"
        target="_blank"
      >
        Open in a new tab
        <FontAwesomeIcon
          className="h-2.5 w-2.5"
          icon={faArrowUpRightFromSquare}
        />
      </a>
    )}
  </div>
)

const Spinner = ({ label }) => (
  <div className="flex h-full flex-col items-center justify-center gap-3">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
    <p className="text-xs text-gray-500">{label}</p>
  </div>
)

/*
  The video itself, once the API says the link works. The API can't vouch for
  everything — ViewMedica, for one, answers 200 for an unknown video and shows
  its own "not found" inside the player — so the player still has to load
  within LOAD_TIMEOUT_MS or the link is reported broken.
*/
const PlayerPreview = ({ link, openLink, title }) => {
  const [state, setState] = useState("loading")

  useEffect(() => {
    if (state !== "loading") return undefined
    const timeoutId = setTimeout(() => setState("timeout"), LOAD_TIMEOUT_MS)
    return () => clearTimeout(timeoutId)
  }, [state])

  if (state === "timeout") {
    return (
      <Notice
        icon={faLinkSlash}
        link={openLink}
        title="Broken link"
        tone="text-red-500"
      >
        The video didn&apos;t load within {LOAD_TIMEOUT_MS / 1000} seconds.
      </Notice>
    )
  }

  return (
    <div className="relative h-full w-full">
      <iframe
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        className="h-full w-full"
        frameBorder="0"
        referrerPolicy="origin"
        src={link}
        title={title}
        onLoad={() => setState("loaded")}
      />
      {state === "loading" && (
        <div className="absolute inset-0 bg-white">
          <Spinner label="Loading video…" />
        </div>
      )}
    </div>
  )
}

const CheckedPlayerPreview = ({
  videoId,
  link,
  openLink,
  title,
  useLinkStatus,
}) => {
  const { data, isLoading, isError } = useLinkStatus(videoId, {
    enabled: true,
  })

  if (isLoading) return <Spinner label="Checking link…" />

  if (data?.status === linkStatuses.BROKEN) {
    return (
      <Notice
        icon={faLinkSlash}
        link={openLink}
        title="Broken link"
        tone="text-red-500"
      >
        {data.reason}
      </Notice>
    )
  }

  if (data?.status === linkStatuses.NOT_EMBEDDABLE) {
    return (
      <Notice
        icon={faTriangleExclamation}
        link={openLink}
        title="Can't preview here"
        tone="text-amber-500"
      >
        {data.reason}
      </Notice>
    )
  }

  // A failed check (isError) isn't proof the link is bad: try the player,
  // whose own timeout still catches a dead link.
  return isError || data ? (
    <PlayerPreview link={link} openLink={openLink} title={title} />
  ) : null
}

// Without a link check the player goes straight in; its load timeout is
// then the only broken-link signal.
const TooltipBody = ({ useLinkStatus, ...props }) =>
  useLinkStatus ? (
    <CheckedPlayerPreview useLinkStatus={useLinkStatus} {...props} />
  ) : (
    <PlayerPreview
      link={props.link}
      openLink={props.openLink}
      title={props.title}
    />
  )

/*
  A video link that previews the video in a tooltip on hover.

  Opens after OPEN_DELAY_MS of hovering, stays open while the mouse is over
  the tooltip (so the video can be played), and closes on scroll — the
  tooltip is fixed to the window and would otherwise drift from its row.
  Rendered in a portal so the table's scroll container can't clip it.

  `useLinkStatus(videoId, { enabled })` is an optional server-side link check
  run before the player loads (the event videos list passes one; Video
  Analytics doesn't). `children` is what the anchor shows (the link itself
  when omitted).

  `link` is what plays in the tooltip; `openLink` is where clicking takes the
  user (the name, "Open in …" and the broken-link notices), when that should
  be a different page — e.g. ViewMedica's share page instead of the embed
  URL. Defaults to `link`.
*/
const VideoLinkPreview = ({
  videoId,
  link,
  openLink,
  title,
  useLinkStatus,
  className = "block truncate text-blue-600 hover:underline",
  children,
}) => {
  const anchorRef = useRef(null)
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)
  const [position, setPosition] = useState(null)
  const href = openLink || link

  const clearTimers = () => {
    clearTimeout(openTimerRef.current)
    clearTimeout(closeTimerRef.current)
  }

  const scheduleOpen = () => {
    clearTimers()
    openTimerRef.current = setTimeout(() => {
      if (anchorRef.current) setPosition(placeTooltip(anchorRef.current))
    }, OPEN_DELAY_MS)
  }

  const scheduleClose = () => {
    clearTimers()
    closeTimerRef.current = setTimeout(() => setPosition(null), CLOSE_DELAY_MS)
  }

  useEffect(() => clearTimers, [])

  useEffect(() => {
    if (!position) return undefined
    const close = () => setPosition(null)
    window.addEventListener("scroll", close, true)
    window.addEventListener("resize", close)
    return () => {
      window.removeEventListener("scroll", close, true)
      window.removeEventListener("resize", close)
    }
  }, [position])

  return (
    <>
      <a
        ref={anchorRef}
        className={className}
        href={href}
        rel="noreferrer"
        target="_blank"
        onBlur={scheduleClose}
        onFocus={scheduleOpen}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
      >
        {children ?? link}
      </a>

      {position &&
        createPortal(
          <div
            className="fixed z-50 flex w-96 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            role="tooltip"
            style={{ left: position.left, top: position.top }}
            onMouseEnter={clearTimers}
            onMouseLeave={scheduleClose}
          >
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2">
              <span className="truncate text-xs font-medium text-gray-700">
                {title}
              </span>
              <a
                className="inline-flex shrink-0 items-center gap-1 text-xs text-blue-600 hover:underline"
                href={href}
                rel="noreferrer"
                target="_blank"
              >
                {getOpenLabel(href)}
                <FontAwesomeIcon
                  className="h-2.5 w-2.5"
                  icon={faArrowUpRightFromSquare}
                />
              </a>
            </div>
            <div className="aspect-video w-full bg-gray-50">
              <TooltipBody
                link={link}
                openLink={href}
                title={title}
                useLinkStatus={useLinkStatus}
                videoId={videoId}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default VideoLinkPreview
